import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from "lucide-react";
import { useApi } from "../api";
import { useCurrentUser } from "../auth";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Select,
  SelectItem,
  Alert,
  Empty,
  Loading,
} from "../components/ui";
import { BASE_LANGUAGE_OPTIONS } from "../constants/languages";
import {
  type ProductRecord,
  type BatchResult,
  languageName,
  languageStatus,
  generationEligible,
} from "../work/types";
import "../work/work.css";

export default function Products() {
  const api = useApi();
  const { t, i18n } = useTranslation("work");
  const { permissions } = useCurrentUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [attention, setAttention] = useState(params.get("attention") === "1");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [sort, setSort] = useState("updated_at:desc");
  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<Map<string, ProductRecord>>(
    new Map(),
  );
  const [target, setTarget] = useState("fr");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [websiteChoice, setWebsiteChoice] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const batchKey = useRef<{ payload: string; key: string } | null>(null);
  const canTranslate = permissions.includes("translation.translate");
  const canCreate = permissions.includes("product.create");
  const canWebsite = permissions.includes("product.embed_manage");
  const queryParams = useCallback(
    (p = page, s = size) =>
      new URLSearchParams({
        scope: "all",
        query,
        page: String(p),
        size: String(s),
        sort_by: sort.split(":")[0],
        sort_dir: sort.split(":")[1],
        ...(attention ? { attention: "true" } : {}),
      }),
    [query, page, size, sort, attention],
  );
  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError("");
      try {
        const response = await api.getResponse(`/products?${queryParams()}`, {
          signal,
        });
        const rows = await response.json();
        if (signal?.aborted) return;
        setProducts(rows);
        setTotal(Number(response.headers.get("x-total-count") || 0));
      } catch (e) {
        if (!signal?.aborted) setError(t("loadError"));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [api, queryParams, t],
  );
  useEffect(() => {
    const abort = new AbortController();
    const timer = setTimeout(() => void load(abort.signal), 200);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [load]);
  useEffect(() => {
    if (permissions.includes("stats.usage"))
      api
        .get("/stats/actions-usage")
        .then((x) => setBalance(x.TRANSLATION_REQUEST?.available ?? null))
        .catch(() => setBalance(null));
  }, [api, permissions]);
  useEffect(() => {
    if (params.get("focus") === "search") input.current?.focus();
  }, [params]);
  useEffect(() => {
    const focus = () => input.current?.focus();
    window.addEventListener("validlabels:focus-search", focus);
    return () => window.removeEventListener("validlabels:focus-search", focus);
  }, []);
  const changeQuery = (value: string) => {
    setQuery(value);
    setPage(1);
    setSelected(new Map());
    setResults([]);
    setParams(
      {
        ...(value ? { q: value } : {}),
        ...(attention ? { attention: "1" } : {}),
      },
      { replace: true },
    );
  };
  const changeAttention = (value: boolean) => {
    setAttention(value);
    setPage(1);
    setSelected(new Map());
    setResults([]);
    setParams(
      { ...(query ? { q: query } : {}), ...(value ? { attention: "1" } : {}) },
      { replace: true },
    );
  };
  const sortDirection = (column: "sku" | "updated_at") =>
    sort === `${column}:asc`
      ? "ascending"
      : sort === `${column}:desc`
        ? "descending"
        : "none";
  const sortControl = (column: "sku" | "updated_at", label: string) => {
    const direction = sortDirection(column);
    const next =
      direction === "ascending"
        ? "desc"
        : direction === "descending"
          ? "asc"
          : column === "updated_at"
            ? "desc"
            : "asc";
    const Icon =
      direction === "ascending"
        ? ArrowUp
        : direction === "descending"
          ? ArrowDown
          : ArrowUpDown;
    return (
      <Button
        variant="ghost"
        size="sm"
        className="catalog-sort"
        aria-label={t(
          next === "asc" ? "catalog.sortAscending" : "catalog.sortDescending",
          { column: label },
        )}
        onClick={() => {
          setSort(`${column}:${next}`);
          setPage(1);
        }}
      >
        {label}
        <Icon size={14} aria-hidden="true" />
      </Button>
    );
  };
  const renderLanguages = (product: ProductRecord) => (
    <>
      <div className="language-list">
        {(product.translation_languages || []).map((code) => {
          const state = product.language_states?.find(
            (x) => x.language === code,
          );
          const status = languageStatus(
            state,
            (product.translation_languages || []).includes(
              product.base_language,
            ),
          );
          return (
            <Link
              className="language-code"
              data-status={status}
              title={`${languageName(code, i18n.language)} · ${t(`status.${status}`)}`}
              aria-label={`${languageName(code, i18n.language)}: ${t(`status.${status}`)}`}
              key={code}
              to={`/products/${product.id}?language=${code}`}
            >
              {code.toUpperCase()}
            </Link>
          );
        })}
        {!product.translation_languages?.includes(product.base_language) && (
          <span className="attention-count">{t("missingSource")}</span>
        )}
      </div>
      <div className="language-summary">
        {product.language_states?.some(
          (x) => languageStatus(x) === "needs_attention",
        )
          ? t("status.needs_attention")
          : product.language_states?.some(
                (x) => languageStatus(x) === "outdated",
              )
            ? t("status.outdated")
            : product.language_states?.length
              ? t("status.up_to_date")
              : t("missingSource")}
      </div>
    </>
  );
  const toggle = (product: ProductRecord, checked: boolean) =>
    setSelected((old) => {
      const next = new Map(old);
      if (checked && next.size < 200) next.set(product.id, product);
      else next.delete(product.id);
      return next;
    });
  const allPage =
    products.length > 0 && products.every((p) => selected.has(p.id));
  const selectAll = async () => {
    setBusy(true);
    setError("");
    try {
      const next = new Map<string, ProductRecord>();
      for (let p = 1; p <= Math.ceil(Math.min(total, 200) / 100); p++) {
        const rows = await api.get(`/products?${queryParams(p, 100)}`);
        rows.forEach((row: ProductRecord) => next.set(row.id, row));
      }
      setSelected(next);
    } catch {
      setError(t("loadError"));
    } finally {
      setBusy(false);
    }
  };
  const eligible = [...selected.values()].filter((product) =>
    generationEligible(product, target),
  );
  // The choice names a product count, so it cannot outlive that selection.
  useEffect(() => setWebsiteChoice(false), [selected]);
  const runBatch = async () => {
    if (!eligible.length || busy) return;
    setBusy(true);
    setError("");
    setResults([]);
    const payload = {
      product_ids: eligible.map((p) => p.id),
      target_language: target,
    };
    const serialized = JSON.stringify(payload);
    if (batchKey.current?.payload !== serialized)
      batchKey.current = { payload: serialized, key: crypto.randomUUID() };
    try {
      const data = await api.post("/translations/bulk-translate", payload, {
        headers: { "Idempotency-Key": batchKey.current.key },
      });
      setSelected(
        (old) =>
          new Map(
            [...old].map(([id, p]) => [
              id,
              (data.results || []).some(
                (r: BatchResult) =>
                  r.product_id === id && r.status === "translated",
              )
                ? {
                    ...p,
                    translation_languages: [
                      ...(p.translation_languages || []),
                      target,
                    ],
                  }
                : p,
            ]),
          ),
      );
      setResults([
        ...(data.results || []),
        ...[...selected.values()]
          .filter((p) => !generationEligible(p, target))
          .map((p) => ({
            product_id: p.id,
            product_name: p.name,
            status: "skipped",
          })),
      ]);
      batchKey.current = null;
      await load();
      if (balance !== null) setBalance(balance - (data.translated_count || 0));
    } catch (e) {
      setError(t("batchError"));
    } finally {
      setBusy(false);
    }
  };
  const websiteBatch = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await api.post("/products/embeds/bulk-publish", {
        product_ids: [...selected.keys()],
      });
      setResults(data.results || []);
      setWebsiteChoice(false);
      await load();
    } catch {
      setError(t("actionError"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="work-page">
      <header className="work-header">
        <div>
          <h1>{t("catalog.title")}</h1>
          <p className="work-subtitle">
            {t("catalog.count", { count: total })}
          </p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/products/new">{t("catalog.new")}</Link>
          </Button>
        )}
      </header>
      {error && (
        <Alert variant="error">
          {error}{" "}
          <Button variant="outline" size="sm" onClick={() => void load()}>
            {t("retry")}
          </Button>
        </Alert>
      )}
      {!!selected.size && (
        <Card className="bulk-panel" aria-label={t("catalog.selection")}>
          <div className="work-actions">
            <strong>{t("catalog.selected", { count: selected.size })}</strong>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setSelected(new Map());
                setResults([]);
              }}
            >
              {t("catalog.clear")}
            </Button>
            {selected.size < total && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void selectAll()}
              >
                {t("catalog.selectAll", { count: Math.min(total, 200) })}
              </Button>
            )}
          </div>
          {canTranslate && (
            <>
              <div className="work-toolbar">
                <label htmlFor="bulk-language">
                  {t("catalog.addLanguage")}
                </label>
                <Select
                  id="bulk-language"
                  value={target}
                  disabled={busy}
                  onValueChange={(value) => {
                    setTarget(value);
                    setResults([]);
                  }}
                  style={{ width: "auto" }}
                >
                  {BASE_LANGUAGE_OPTIONS.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {languageName(l.code, i18n.language)}
                    </SelectItem>
                  ))}
                </Select>
                <Button
                  disabled={
                    busy ||
                    eligible.length === 0 ||
                    (balance !== null && balance < eligible.length)
                  }
                  onClick={() => void runBatch()}
                >
                  {busy
                    ? t("working")
                    : t("catalog.translateCost", { count: eligible.length })}
                </Button>
              </div>
              <p className="work-muted">
                {t("catalog.batchScope", {
                  eligible: eligible.length,
                  skipped: selected.size - eligible.length,
                })}{" "}
                {total > 200 && t("catalog.batchLimit")} {t("websiteTiming")}{" "}
                {balance !== null && t("creditsAvailable", { count: balance })}
              </p>
            </>
          )}
          {canWebsite && (
            <details className="bulk-website">
              <summary>{t("website")}</summary>
              <label className="work-inline-note">
                <Checkbox
                  checked={websiteChoice}
                  disabled={busy}
                  onCheckedChange={(nextChecked) =>
                    setWebsiteChoice(nextChecked === true)
                  }
                />
                <span>
                  {t("catalog.websiteChoice", { count: selected.size })}
                </span>
              </label>
              <Button
                variant="outline"
                size="sm"
                disabled={!websiteChoice || busy}
                onClick={() => void websiteBatch()}
              >
                {t("turnOnWebsite")}
              </Button>
            </details>
          )}
          {busy && <p role="status">{t("catalog.batchWorking")}</p>}
          {!!results.length && (
            <div role="status">
              <h2>{t("catalog.results")}</h2>
              <ul className="batch-results">
                {results.map((r) => (
                  <li key={r.product_id}>
                    <Link to={`/products/${r.product_id}`}>
                      {r.product_name}
                    </Link>
                    <span>
                      {t(`outcome.${r.status}`, {
                        defaultValue: t("outcome.failed"),
                      })}
                      {(r.skip_reason || r.error_detail) && (
                        <small className="work-muted">
                          {" "}
                          ·{" "}
                          {t(`reason.${r.skip_reason}`, {
                            defaultValue: r.error_detail
                              ? t("catalog.failedCorrection")
                              : t("catalog.skipped"),
                          })}
                        </small>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
      <Card className="catalog-card">
        <div className="catalog-toolbar">
          <div className="vl-tabs">
            <button
              type="button"
              className="vl-tab"
              aria-pressed={!attention}
              onClick={() => changeAttention(false)}
            >
              {t("catalog.all")}
            </button>
            <button
              type="button"
              className="vl-tab"
              aria-pressed={attention}
              onClick={() => changeAttention(true)}
            >
              {t("status.needs_attention")}
            </button>
          </div>
          <Input
            ref={input}
            type="search"
            aria-label={t("catalog.search")}
            placeholder={t("catalog.search")}
            value={query}
            onChange={(e) => changeQuery(e.target.value)}
          />
        </div>
      {loading && !products.length ? (
        <Loading label={t("loading")} />
      ) : !products.length && !error ? (
        <Empty
          title={t(query ? "catalog.noMatches" : "catalog.empty")}
          description={t(query ? "catalog.searchHelp" : "catalog.emptyHelp")}
        >
          {query ? (
            <Button variant="outline" onClick={() => changeQuery("")}>
              {t("catalog.clearSearch")}
            </Button>
          ) : (
            canCreate && (
              <Button asChild>
                <Link to="/products/new">{t("catalog.new")}</Link>
              </Button>
            )
          )}
        </Empty>
      ) : (
        <>
          <div className="work-table-wrap" aria-busy={loading}>
            <table className="catalog-table">
              <caption className="sr-only">{t("catalog.title")}</caption>
              <thead>
                <tr>
                  <th scope="col" className="catalog-selection">
                    <Checkbox
                      className="work-check"
                      aria-label={t("catalog.selectPage")}
                      checked={
                        allPage
                          ? true
                          : products.some((product) => selected.has(product.id))
                            ? "indeterminate"
                            : false
                      }
                      disabled={busy}
                      onCheckedChange={(nextChecked) => {
                        const checked = nextChecked === true;
                        setSelected((old) => {
                          const next = new Map(old);
                          products.forEach((p) => {
                            if (!checked) next.delete(p.id);
                            else if (next.size < 200) next.set(p.id, p);
                          });
                          return next;
                        });
                      }}
                    />
                  </th>
                  <th scope="col" className="catalog-product">
                    {t("product")}
                  </th>
                  <th
                    scope="col"
                    className="catalog-sku"
                    aria-sort={sortDirection("sku")}
                  >
                    {sortControl("sku", t("identity.sku"))}
                  </th>
                  <th scope="col" className="catalog-languages">
                    {t("languages")}
                  </th>
                  <th
                    scope="col"
                    className="catalog-updated"
                    aria-sort={sortDirection("updated_at")}
                  >
                    {sortControl("updated_at", t("catalog.updated"))}
                  </th>
                  <th scope="col" className="catalog-action">
                    <span className="sr-only">{t("catalog.open")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr
                    key={product.id}
                    onClick={(event) => {
                      if (
                        !(event.target as HTMLElement).closest(
                          "a,button,input,label,.catalog-selection",
                        )
                      )
                        navigate(`/products/${product.id}`);
                    }}
                  >
                    <td className="catalog-selection">
                      <Checkbox
                        className="work-check"
                        aria-label={t("catalog.selectProduct", {
                          name: product.name,
                        })}
                        checked={selected.has(product.id)}
                        disabled={
                          busy ||
                          (!selected.has(product.id) && selected.size >= 200)
                        }
                        onCheckedChange={(nextChecked) =>
                          toggle(product, nextChecked === true)
                        }
                      />
                    </td>
                    <td className="catalog-product">
                      <Link to={`/products/${product.id}`}>{product.name}</Link>
                      {product.ean && (
                        <div className="catalog-identity">
                          EAN {product.ean}
                        </div>
                      )}
                      <div className="catalog-matches">
                        {product.search_matches?.map((match, index) => (
                          <Link
                            key={index}
                            to={`/products/${product.id}?language=${encodeURIComponent(match.language)}`}
                          >
                            <span>
                              {t("catalog.matchIn", {
                                language: languageName(
                                  match.language,
                                  i18n.language,
                                ),
                              })}
                              {match.code ? ` · ${match.code}` : ""}
                            </span>
                            <span>{match.text}</span>
                          </Link>
                        ))}
                      </div>
                      <div className="catalog-languages-mobile">
                        {renderLanguages(product)}
                      </div>
                    </td>
                    <td className="catalog-sku">
                      {product.sku || (
                        <span className="work-muted">{t("noSku")}</span>
                      )}
                    </td>
                    <td className="catalog-languages">
                      {renderLanguages(product)}
                    </td>
                    <td className="catalog-updated">
                      {product.updated_at ? (
                        <time
                          dateTime={product.updated_at}
                          title={new Date(product.updated_at).toLocaleString(
                            i18n.language,
                          )}
                        >
                          {new Date(product.updated_at).toLocaleDateString(
                            i18n.language,
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "2-digit",
                            },
                          )}
                        </time>
                      ) : (
                        <span className="work-muted">—</span>
                      )}
                    </td>
                    <td className="catalog-action">
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="catalog-open vl-button-icon"
                      >
                        <Link
                          to={`/products/${product.id}`}
                          aria-label={t("catalog.openProduct", {
                            name: product.name,
                          })}
                          title={t("catalog.openProduct", {
                            name: product.name,
                          })}
                        >
                          <ChevronRight aria-hidden="true" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="work-pagination">
            <span>
              {t("catalog.range", {
                from: total ? (page - 1) * size + 1 : 0,
                to: Math.min(page * size, total),
                total,
              })}
            </span>
            <div className="work-actions">
              <label className="work-actions">
                {t("catalog.perPage")}
                <Select
                  value={size}
                  onValueChange={(value) => {
                    setSize(Number(value));
                    setPage(1);
                  }}
                >
                  {[25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </Select>
              </label>
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || loading}
                onClick={() => setPage(page - 1)}
              >
                {t("previous")}
              </Button>
              <span>
                {page} / {Math.max(1, Math.ceil(total / size))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page * size >= total || loading}
                onClick={() => setPage(page + 1)}
              >
                {t("next")}
              </Button>
            </div>
          </div>
        </>
      )}
      </Card>
    </div>
  );
}
